<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { ESeoHeadingSize } from './ts/types'
import { useBrxmData } from '@/cms-ui/composables/useBrxmData'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { useImage } from '@/cms-ui/composables/useImageUrl'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()
const data = useBrxmData(props, 'LEAD_FORM')
const leadFormRef = useTemplateRef('leadFormRef')

const {
    darkmode,
    background,
    heading,
    seoHeadingSize,
    paragraph,
    link,
    imageOrVideo,
    mobileImageOrVideoOff,
    formSection,
    consentsSection,
    legalTitle,
    legalDescription,
    actionTitle,
    legalReadmore,
    drawerType,
    successContent,
    errorContent,
} = data.value

useVisibilityTracking(leadFormRef, 'LeadForm', heading || 'LeadForm')

const { image: responsiveImage } = useImage(imageOrVideo, props.page)
</script>

<template>
    <LeadFormPartsFormSection
        v-if="drawerType"
        :heading="heading"
        :seo-heading-size="formSection?.seoHeadingSize?.value"
        :paragraph="paragraph?.value"
        :responsive-image="responsiveImage"
        :image-alt="imageOrVideo?.altText"
        :title="formSection?.title"
        :subjects-group-title="formSection?.subjectsGroupTitle"
        :subjects="formSection?.subjects"
        :consents-title="consentsSection?.consentsTitle"
        :consents="consentsSection?.consents"
        :consents-validation="consentsSection?.validation"
        :labels="consentsSection?.labels"
        :legal-title="legalTitle"
        :legal-readmore="legalReadmore"
        :legal-description="legalDescription?.value"
        :action-title="actionTitle"
        :drawer-type="drawerType"
        :success-content="successContent"
        :error-content="errorContent"
    />
    <section
        v-else
        ref="leadFormRef"
        class="space-standard container"
    >
        <BackgroundBlock
            is="div"
            class="bg-neutral-secondary rounded-container-lg pt-lg px-sm md:px-md pb-sm md:py-xl lg:py-2xl"
            :class="{ dark: darkmode }"
            :page="page"
            :background="background"
        >
            <div class="container-grid gap-y-lg">
                <div class="lg:pr-xl max-md:px-sm col-span-4 flex flex-col md:col-span-3 lg:col-span-5 lg:col-start-2">
                    <component
                        :is="seoHeadingSize?.value || ESeoHeadingSize.H3"
                        class="heading-md lg:heading-2xl text-comp-high-contrast line-clamp-3"
                    >
                        {{ heading }}
                    </component>
                    <div
                        v-if="paragraph"
                        v-html-gpp="paragraph.value"
                        class="mt-xs paragraph-md lg:paragraph-lg text-neutral-primary"
                    />
                    <VLink
                        v-if="link"
                        :to="link.action?.url"
                        class="mt-base md:mt-md text-comp-high-contrast"
                        :dark="darkmode"
                        size="LARGE"
                        data-test="lead-form"
                        :variant="link.visualVariant"
                        :target="link.action?.openInNewTab ? '_blank' : '_self'"
                    >
                        {{ link.label }}
                    </VLink>
                    <BaseImage
                        v-if="imageOrVideo"
                        picture-classes="mt-lg md:mt-xl inline-block self-center"
                        :class="{ 'max-md:hidden': mobileImageOrVideoOff }"
                        :image-data="responsiveImage"
                        :alt="imageOrVideo.altText"
                    />
                </div>
                <div class="light col-span-4 md:col-span-5 lg:col-span-5 lg:col-start-7">
                    <div class="bg-neutral-primary py-md px-sm md:p-md lg:p-lg rounded-container-lg">
                        <LeadFormPartsFormSection
                            :title="formSection?.title"
                            :seo-heading-size="formSection?.seoHeadingSize?.value"
                            :subjects-group-title="formSection?.subjectsGroupTitle"
                            :subjects="formSection?.subjects"
                            :consents-title="consentsSection?.consentsTitle"
                            :consents="consentsSection?.consents"
                            :consents-validation="consentsSection?.validation"
                            :labels="consentsSection?.labels"
                            :legal-title="legalTitle"
                            :legal-readmore="legalReadmore"
                            :legal-description="legalDescription?.value"
                            :action-title="actionTitle"
                            :success-content="successContent"
                            :error-content="errorContent"
                        />
                    </div>
                </div>
            </div>
        </BackgroundBlock>
    </section>
</template>
